/**
 * skillOntology.js
 * --------------------------------------------------------------------------
 * The master skill vocabulary + role definitions used by the CV parser and
 * the profile match engine.
 *
 * MULTILINGUAL: each skill carries labels in 5 EU languages (English, German,
 * French, Spanish, Italian). The `aliases` array is language-agnostic, it
 * pools terms from ALL languages so a French CV scanned by the parser
 * matches the same skill node as an English CV. The UI surfaces the label
 * in whatever language the user has selected via the language switcher.
 *
 * BIAS / EU AI ACT POSTURE: aliases are deliberately neutral. We avoid
 * gender-coded vocabulary that has historically biased skill-matching tools
 * (e.g. "ninja", "rockstar", "guru", "aggressive", "dominate"). Skill nodes
 * are tied to demonstrable competencies, not personality framings. The list
 * is reviewed quarterly per Art. 10 (Data Governance) of the EU AI Act,
 * see /src/data/biasReviewLog.js for the audit trail.
 *
 * In production this ontology would come from the customer's own
 * competency framework (synced from Workday Job Profiles, SuccessFactors
 * Position Catalog, or imported via Merge.dev's HRIS unified API).
 */

const skill = (id, labels, aliases) => ({ id, ...labels, aliases });

export const ALL_SKILLS = [
  // Engineering / technical
  skill('js',
    { en: 'JavaScript', de: 'JavaScript', fr: 'JavaScript', es: 'JavaScript', it: 'JavaScript' },
    ['javascript', 'js', 'ecmascript']),
  skill('ts',
    { en: 'TypeScript', de: 'TypeScript', fr: 'TypeScript', es: 'TypeScript', it: 'TypeScript' },
    ['typescript', 'ts']),
  skill('react',
    { en: 'React', de: 'React', fr: 'React', es: 'React', it: 'React' },
    ['react.js', 'reactjs', 'react']),
  skill('vue',
    { en: 'Vue', de: 'Vue', fr: 'Vue', es: 'Vue', it: 'Vue' },
    ['vue.js', 'vuejs', 'vue']),
  skill('angular',
    { en: 'Angular', de: 'Angular', fr: 'Angular', es: 'Angular', it: 'Angular' },
    ['angular', 'angularjs']),
  skill('node',
    { en: 'Node.js', de: 'Node.js', fr: 'Node.js', es: 'Node.js', it: 'Node.js' },
    ['node.js', 'nodejs', 'node']),
  skill('python',
    { en: 'Python', de: 'Python', fr: 'Python', es: 'Python', it: 'Python' },
    ['python']),
  skill('java',
    { en: 'Java', de: 'Java', fr: 'Java', es: 'Java', it: 'Java' },
    ['java']),
  skill('go',
    { en: 'Go', de: 'Go', fr: 'Go', es: 'Go', it: 'Go' },
    ['golang', 'go']),
  skill('rust',
    { en: 'Rust', de: 'Rust', fr: 'Rust', es: 'Rust', it: 'Rust' },
    ['rust']),
  skill('sql',
    { en: 'SQL', de: 'SQL', fr: 'SQL', es: 'SQL', it: 'SQL' },
    ['sql', 'mysql', 'postgresql', 'postgres']),
  skill('aws',
    { en: 'AWS', de: 'AWS', fr: 'AWS', es: 'AWS', it: 'AWS' },
    ['aws', 'amazon web services']),
  skill('gcp',
    { en: 'Google Cloud', de: 'Google Cloud', fr: 'Google Cloud', es: 'Google Cloud', it: 'Google Cloud' },
    ['gcp', 'google cloud']),
  skill('azure',
    { en: 'Azure', de: 'Azure', fr: 'Azure', es: 'Azure', it: 'Azure' },
    ['azure', 'microsoft azure']),
  skill('k8s',
    { en: 'Kubernetes', de: 'Kubernetes', fr: 'Kubernetes', es: 'Kubernetes', it: 'Kubernetes' },
    ['kubernetes', 'k8s']),
  skill('docker',
    { en: 'Docker', de: 'Docker', fr: 'Docker', es: 'Docker', it: 'Docker' },
    ['docker']),
  skill('cicd',
    { en: 'CI/CD', de: 'CI/CD', fr: 'CI/CD', es: 'CI/CD', it: 'CI/CD' },
    ['ci/cd', 'cicd', 'continuous integration', 'continuous deployment',
     'integration continue', 'integración continua', 'integrazione continua']),
  skill('systemDesign',
    { en: 'System Design', de: 'Systemarchitektur', fr: 'Architecture système', es: 'Arquitectura de sistemas', it: 'Architettura dei sistemi' },
    ['system design', 'systemarchitektur', 'architecture systeme', 'architecture système',
     'arquitectura de sistemas', 'architettura dei sistemi']),
  skill('codeReview',
    { en: 'Code Review', de: 'Code Review', fr: 'Revue de code', es: 'Revisión de código', it: 'Revisione del codice' },
    ['code review', 'peer review', 'revue de code', 'revision de codigo', 'revisión de código',
     'revisione del codice']),
  skill('agile',
    { en: 'Agile', de: 'Agile', fr: 'Agile', es: 'Ágil', it: 'Agile' },
    ['agile', 'scrum', 'kanban', 'agil', 'ágil']),
  skill('mentoring',
    { en: 'Mentoring', de: 'Mentoring', fr: 'Mentorat', es: 'Mentoría', it: 'Mentoring' },
    ['mentoring', 'mentor', 'mentee', 'coaching', 'mentorat', 'mentoria', 'mentoría',
     'tutoring', 'tutoraggio']),

  // Sales / Enterprise / Business
  skill('enterpriseSales',
    { en: 'Enterprise Sales', de: 'Enterprise-Vertrieb', fr: 'Ventes Entreprises', es: 'Ventas Empresariales', it: 'Vendite Enterprise' },
    ['enterprise sales', 'enterprise-vertrieb', 'b2b sales', 'enterprise selling',
     'ventes entreprises', 'ventes b2b', 'ventas empresariales', 'ventas b2b',
     'vendite enterprise', 'vendite b2b']),
  skill('accountManagement',
    { en: 'Account Management', de: 'Account Management', fr: 'Gestion de comptes', es: 'Gestión de cuentas', it: 'Gestione clienti' },
    ['account management', 'key account', 'kundenbetreuung',
     'gestion de comptes', 'gestion compte', 'gestion de cuentas', 'gestion de clientes',
     'gestione clienti', 'gestione account']),
  skill('pipelineManagement',
    { en: 'Pipeline Management', de: 'Pipeline-Management', fr: 'Gestion du pipeline', es: 'Gestión del pipeline', it: 'Gestione della pipeline' },
    ['pipeline management', 'sales pipeline', 'forecast', 'gestion du pipeline',
     'gestion del pipeline', 'gestione della pipeline']),
  skill('negotiation',
    { en: 'Negotiation', de: 'Verhandlungsführung', fr: 'Négociation', es: 'Negociación', it: 'Negoziazione' },
    ['negotiation', 'verhandlung', 'verhandlungsführung', 'verhandlungsfuhrung',
     'negociation', 'négociation', 'negociacion', 'negociación', 'negoziazione']),
  skill('crm',
    { en: 'CRM (Salesforce)', de: 'CRM (Salesforce)', fr: 'CRM (Salesforce)', es: 'CRM (Salesforce)', it: 'CRM (Salesforce)' },
    ['crm', 'salesforce', 'hubspot', 'dynamics']),
  skill('territoryPlanning',
    { en: 'Territory Planning', de: 'Vertriebsgebietsplanung', fr: 'Planification de territoire', es: 'Planificación de territorio', it: 'Pianificazione del territorio' },
    ['territory planning', 'vertriebsgebiet', 'territory',
     'planification de territoire', 'planificacion de territorio', 'planificación de territorio',
     'pianificazione del territorio']),
  skill('channelPartner',
    { en: 'Channel & Partner', de: 'Channel & Partner', fr: 'Canaux & Partenaires', es: 'Canales y Socios', it: 'Canali e Partner' },
    ['channel', 'partner sales', 'channel partner', 'reseller',
     'partenaires', 'canales', 'socios', 'canali']),
  skill('rfp',
    { en: 'RFP / Tender', de: 'Ausschreibungen / RFP', fr: "Appels d'offres / RFP", es: 'Licitaciones / RFP', it: "Gare d'appalto / RFP" },
    ['rfp', 'rfi', 'tender', 'ausschreibung', "appel d'offres", "appels d'offres",
     'licitacion', 'licitación', 'licitaciones', "gara d'appalto", "gare d'appalto"]),
  skill('csuite',
    { en: 'C-Suite Engagement', de: 'C-Level-Ansprache', fr: 'Engagement C-Suite', es: 'Relación con C-Suite', it: 'Coinvolgimento C-Suite' },
    ['c-suite', 'c-level', 'cxo', 'executive sponsor',
     'direction generale', 'direction générale', 'comite ejecutivo', 'comité ejecutivo',
     'comitato esecutivo']),
  skill('saas',
    { en: 'SaaS', de: 'SaaS', fr: 'SaaS', es: 'SaaS', it: 'SaaS' },
    ['saas', 'software as a service', 'logiciel en tant que service']),
  skill('p2p',
    { en: 'Procurement Process', de: 'Beschaffungsprozess', fr: "Processus d'achat", es: 'Proceso de compras', it: 'Processo di acquisto' },
    ['procurement', 'p2p', 'beschaffung', 'einkauf',
     'achats', "processus d'achat", 'compras', 'proceso de compras',
     'acquisti', 'processo di acquisto']),
  skill('pAndL',
    { en: 'P&L Ownership', de: 'P&L-Verantwortung', fr: 'Responsabilité P&L', es: 'Responsabilidad P&L', it: 'Responsabilità P&L' },
    ['p&l', 'p and l', 'profit and loss', 'p&l-verantwortung',
     'responsabilite p&l', 'responsabilité p&l', 'compte de resultat', 'compte de résultat',
     'cuenta de resultados', 'conto economico']),
  skill('forecastAccuracy',
    { en: 'Forecasting', de: 'Forecasting', fr: 'Prévisions', es: 'Previsiones', it: 'Previsioni' },
    ['forecast', 'forecasting', 'prognose', 'prevision', 'prévision', 'prévisions',
     'previsiones', 'previsioni']),
  skill('teamLeadership',
    { en: 'Team Leadership', de: 'Teamführung', fr: "Leadership d'équipe", es: 'Liderazgo de equipo', it: 'Leadership di team' },
    ['team lead', 'leadership', 'teamführung', 'teamfuhrung', 'führung', 'fuhrung',
     'people management', "leadership d'equipe", "leadership d'équipe",
     'liderazgo', 'liderazgo de equipo', 'leadership di team', 'gestione del team']),
  skill('goToMarket',
    { en: 'Go-to-Market', de: 'Go-to-Market', fr: 'Go-to-Market', es: 'Go-to-Market', it: 'Go-to-Market' },
    ['go-to-market', 'gtm', 'go to market', 'mise sur le marche', 'mise sur le marché']),
  skill('strategicPlanning',
    { en: 'Strategic Planning', de: 'Strategische Planung', fr: 'Planification stratégique', es: 'Planificación estratégica', it: 'Pianificazione strategica' },
    ['strategic planning', 'strategie', 'strategy', 'strategische planung',
     'planification strategique', 'planification stratégique', 'stratégie',
     'planificacion estrategica', 'planificación estratégica',
     'pianificazione strategica', 'strategia']),
  skill('enablement',
    { en: 'Sales Enablement', de: 'Sales Enablement', fr: 'Sales Enablement', es: 'Capacitación de ventas', it: 'Sales Enablement' },
    ['sales enablement', 'enablement', 'capacitacion', 'capacitación', 'capacitación de ventas',
     'formazione vendite']),
  skill('demandGen',
    { en: 'Demand Generation', de: 'Demand Generation', fr: 'Demand Generation', es: 'Generación de demanda', it: 'Demand Generation' },
    ['demand generation', 'demand gen', 'lead generation',
     'generation de demande', 'génération de demande', 'generacion de leads', 'generación de leads',
     'generazione di lead']),

  // HR / People Operations
  skill('compStrategy',
    { en: 'Compensation Strategy', de: 'Vergütungsstrategie', fr: 'Stratégie de rémunération', es: 'Estrategia de compensación', it: 'Strategia retributiva' },
    ['compensation strategy', 'compensation', 'vergütung', 'vergutung', 'vergütungsstrategie',
     'remuneration', 'rémunération', 'strategie de remuneration', 'stratégie de rémunération',
     'compensacion', 'compensación', 'estrategia de compensacion', 'estrategia de compensación',
     'retribuzione', 'strategia retributiva']),
  skill('payEquity',
    { en: 'Pay Equity', de: 'Entgeltgleichheit', fr: 'Équité salariale', es: 'Equidad salarial', it: 'Equità retributiva' },
    ['pay equity', 'entgeltgleichheit', 'pay gap',
     'equite salariale', 'équité salariale', 'ecart salarial', 'écart salarial',
     'equidad salarial', 'brecha salarial', 'equita retributiva', 'equità retributiva',
     'divario retributivo']),
  skill('talentMgmt',
    { en: 'Talent Management', de: 'Talent Management', fr: 'Gestion des talents', es: 'Gestión del talento', it: 'Gestione dei talenti' },
    ['talent management', 'talent', 'talentmanagement', 'gestion des talents',
     'gestion del talento', 'gestión del talento', 'gestione dei talenti']),
  skill('orgDesign',
    { en: 'Organisational Design', de: 'Organisationsentwicklung', fr: 'Conception organisationnelle', es: 'Diseño organizacional', it: 'Progettazione organizzativa' },
    ['org design', 'organisational design', 'organizational design', 'organisationsentwicklung',
     'conception organisationnelle', 'design organisationnel',
     'diseno organizacional', 'diseño organizacional', 'progettazione organizzativa']),
  skill('employeeRelations',
    { en: 'Employee Relations', de: 'Mitarbeiterbeziehungen', fr: 'Relations sociales', es: 'Relaciones laborales', it: 'Relazioni industriali' },
    ['employee relations', 'mitarbeiterbeziehungen', 'works council', 'betriebsrat',
     'relations sociales', 'comite social', 'comité social',
     'relaciones laborales', 'relazioni industriali', 'rappresentanza sindacale']),
  skill('hrAnalytics',
    { en: 'People Analytics', de: 'People Analytics', fr: 'People Analytics', es: 'People Analytics', it: 'People Analytics' },
    ['people analytics', 'hr analytics', 'workforce analytics']),
  skill('workdayHris',
    { en: 'Workday / HRIS', de: 'Workday / HRIS', fr: 'Workday / SIRH', es: 'Workday / HRIS', it: 'Workday / HRIS' },
    ['workday', 'successfactors', 'hris', 'sap hr', 'sirh']),
  skill('gdprHr',
    { en: 'GDPR / Employment Law', de: 'DSGVO / Arbeitsrecht', fr: 'RGPD / Droit du travail', es: 'RGPD / Derecho laboral', it: 'GDPR / Diritto del lavoro' },
    ['gdpr', 'dsgvo', 'rgpd', 'employment law', 'arbeitsrecht', 'droit du travail',
     'derecho laboral', 'diritto del lavoro']),
  skill('change',
    { en: 'Change Management', de: 'Change Management', fr: 'Conduite du changement', es: 'Gestión del cambio', it: 'Gestione del cambiamento' },
    ['change management', 'change', 'conduite du changement',
     'gestion del cambio', 'gestión del cambio', 'gestione del cambiamento']),

  // Product / general business
  skill('productMgmt',
    { en: 'Product Management', de: 'Produktmanagement', fr: 'Gestion de produit', es: 'Gestión de producto', it: 'Product Management' },
    ['product management', 'produktmanagement', 'product manager',
     'gestion de produit', 'chef de produit',
     'gestion de producto', 'gestión de producto',
     'gestione del prodotto']),
  skill('roadmap',
    { en: 'Roadmap Planning', de: 'Roadmap-Planung', fr: 'Planification de roadmap', es: 'Planificación de roadmap', it: 'Pianificazione della roadmap' },
    ['roadmap', 'roadmap planning', 'roadmap-planung',
     'planification de roadmap', 'planificacion de roadmap', 'planificación de roadmap',
     'pianificazione della roadmap']),
  skill('userResearch',
    { en: 'User Research', de: 'Nutzerforschung', fr: 'Recherche utilisateur', es: 'Investigación de usuarios', it: 'Ricerca utenti' },
    ['user research', 'nutzerforschung', 'ux research',
     'recherche utilisateur', 'investigacion de usuarios', 'investigación de usuarios',
     'ricerca utenti']),
  skill('analytics',
    { en: 'Data Analytics', de: 'Datenanalyse', fr: 'Analyse de données', es: 'Análisis de datos', it: 'Analisi dei dati' },
    ['data analytics', 'analytics', 'datenanalyse', 'sql analysis',
     'analyse de donnees', 'analyse de données',
     'analisis de datos', 'análisis de datos', 'analisi dei dati']),
  skill('stakeholder',
    { en: 'Stakeholder Management', de: 'Stakeholder-Management', fr: 'Gestion des parties prenantes', es: 'Gestión de stakeholders', it: 'Gestione degli stakeholder' },
    ['stakeholder', 'stakeholder management',
     'gestion des parties prenantes', 'partes interesadas',
     'gestione degli stakeholder']),
  skill('projectMgmt',
    { en: 'Project Management', de: 'Projektmanagement', fr: 'Gestion de projet', es: 'Gestión de proyectos', it: 'Project Management' },
    ['project management', 'projektmanagement', 'pmp', 'prince2',
     'gestion de projet', 'gestion de proyectos', 'gestión de proyectos',
     'gestione progetti']),
  skill('communication',
    { en: 'Executive Communication', de: 'Executive Communication', fr: 'Communication exécutive', es: 'Comunicación ejecutiva', it: 'Comunicazione esecutiva' },
    ['executive communication', 'presentation', 'public speaking',
     'communication executive', 'communication exécutive',
     'comunicacion ejecutiva', 'comunicación ejecutiva',
     'comunicazione esecutiva']),
];

const role = (id, labels, salary, requiredSkillIds) => ({ id, ...labels, ...salary, requiredSkillIds });

export const ROLES = {
  l5SeniorEngineer: role('l5SeniorEngineer',
    {
      en: 'Senior Software Engineer (L5)',
      de: 'Senior Software Engineer (L5)',
      fr: 'Ingénieur Logiciel Senior (L5)',
      es: 'Ingeniero de Software Senior (L5)',
      it: 'Senior Software Engineer (L5)',
    },
    { bandMin: 65000, bandMid: 80000, bandMax: 95000 },
    ['js', 'ts', 'react', 'node', 'python', 'sql', 'aws', 'systemDesign',
     'codeReview', 'agile', 'mentoring', 'cicd']),
  seniorDirectorEnterprise: role('seniorDirectorEnterprise',
    {
      en: 'Senior Director, Enterprise Business',
      de: 'Senior Director, Enterprise Business',
      fr: 'Directeur Senior, Activité Entreprises',
      es: 'Director Senior, Negocio Empresarial',
      it: 'Senior Director, Enterprise Business',
    },
    { bandMin: 145000, bandMid: 180000, bandMax: 220000 },
    ['enterpriseSales', 'accountManagement', 'pipelineManagement',
     'negotiation', 'crm', 'csuite', 'saas', 'pAndL', 'forecastAccuracy',
     'teamLeadership', 'goToMarket', 'strategicPlanning', 'enablement', 'channelPartner']),
  hrManager: role('hrManager',
    {
      en: 'People Operations Manager',
      de: 'People Operations Manager',
      fr: 'Responsable People Operations',
      es: 'Responsable de Operaciones de Personal',
      it: 'Responsabile People Operations',
    },
    { bandMin: 70000, bandMid: 85000, bandMax: 100000 },
    ['compStrategy', 'payEquity', 'talentMgmt', 'orgDesign',
     'employeeRelations', 'hrAnalytics', 'workdayHris', 'gdprHr', 'change', 'communication']),
  productManager: role('productManager',
    {
      en: 'Product Manager',
      de: 'Product Manager',
      fr: 'Chef de Produit',
      es: 'Gerente de Producto',
      it: 'Product Manager',
    },
    { bandMin: 75000, bandMid: 92000, bandMax: 115000 },
    ['productMgmt', 'roadmap', 'userResearch', 'analytics',
     'stakeholder', 'agile', 'communication', 'strategicPlanning']),
};

export const SKILL_BY_ID = ALL_SKILLS.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

export const SUPPORTED_LANGS = ['en', 'de', 'fr', 'es', 'it'];

export function labelFor(skillOrRole, lang) {
  if (!skillOrRole) return '';
  return skillOrRole[lang] || skillOrRole.en || skillOrRole.id || '';
}
